import { useContext, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../lib/hooks';
import {
    AppContext,
    SttDialog,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttSelectFacility,
    SttSelectUser,
    SttSelectWeekdays,
} from '../../sporttia/all';
import { PERMISSIONS } from '../../lib/permissions';

type User = {
    id: number;
    name?: string;
    fullName?: string;
};

type Facility = {
    id: number;
    name?: string;
};

type Mship = {
    fullName: string;
    user: User;
};

type BookingSchedule = {
    id?: number;
    name?: string;
    user?: User;
    facility: Facility;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    status?: string;
};

type BookingScheduleResponse = {
    bookingSchedule?: BookingSchedule;
};

type CreateBookingScheduleDialogProps = {
    item: BookingSchedule;
    onSave?: () => void;
    onClose?: () => void;
};

/**
 * CreateBookingScheduleDialog : used to create fixed bookings from the timetable section (TimetableOperate component)
 */
export default function CreateBookingScheduleDialog({
    item,
    onSave,
    onClose,
}: CreateBookingScheduleDialogProps): React.ReactElement | null {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [bookingSchedule, , setPropertyRaw, , Post] =
        useCrud<BookingSchedule>(item);

    // Wrapper to adapt useCrud setProperty to form component onChange signature
    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPropertyRaw({
            name: name as keyof BookingSchedule,
            value: value as BookingSchedule[keyof BookingSchedule],
        });
    };
    const [showCreatedInfo, setShowCreatedInfo] = useState(false);
    const [errors, setErrors] = useState<{ name?: string; user?: string }>({});

    // Validate form fields
    function validateForm(): boolean {
        const newErrors: { name?: string; user?: string } = {};

        if (!bookingSchedule?.name?.trim()) {
            newErrors.name = t('validation.required');
        }

        if (!bookingSchedule?.user?.id) {
            newErrors.user = t('validation.required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    // What it says
    function saveAndClose(): void {
        if (onSave && onSave.constructor === Function) {
            onSave();
        }
        if (onClose && onClose.constructor === Function) {
            onClose();
        }
    }

    // Create a new fixed booking via POST
    function save(): void {
        if (!bookingSchedule || !cxt?.sc?.id) return;

        // Validate before saving
        if (!validateForm()) {
            return;
        }

        const params = {
            name: bookingSchedule.name,
            idUser: bookingSchedule.user?.id,
            idFacility: bookingSchedule.facility.id,
            dateini: format(parseISO(bookingSchedule.dateini), 'yyyy-MM-dd'),
            dateend: format(parseISO(bookingSchedule.dateend), 'yyyy-MM-dd'),
            timeini: bookingSchedule.timeini,
            timeend: bookingSchedule.timeend,
            weekdays: bookingSchedule.weekdays,
        };

        Post<BookingScheduleResponse>(
            `/scs/${cxt.sc?.id}/bookings/schedules`,
            params,
        ).then((response: any) => {
            if (response.bookingSchedule) {
                setShowCreatedInfo(true);
            }
        });
    }

    if (!bookingSchedule) {
        return null;
    }

    return (
        <>
            <SttDialog
                open
                onClose={onClose}
                title={t('timetable.createFixedBook')}
                buttons={[
                    {
                        type: 'save',
                        onClick: save,
                        permission: PERMISSIONS.FIXED_BOOKING.CREATE,
                    },
                ]}
                content={
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <SttFormText
                                name="name"
                                caption={t('Name')}
                                defVal={bookingSchedule.name ?? ''}
                                onChange={(e) => {
                                    setProperty(e);
                                    if (errors.name) {
                                        setErrors((prev) => ({
                                            ...prev,
                                            name: undefined,
                                        }));
                                    }
                                }}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <SttFormSelect
                                name="status"
                                caption={t('Status')}
                                defVal={bookingSchedule.status}
                                options={cxt?.constants?.scheduleStatuses || []}
                                onChange={setProperty}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <SttFormDate
                                disabled={!!bookingSchedule.id}
                                caption={t('dateIni')}
                                name="dateini"
                                defVal={bookingSchedule.dateini}
                                onChange={setProperty}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <SttFormDate
                                disabled={!!bookingSchedule.id}
                                caption={t('dateEnd')}
                                name="dateend"
                                defVal={bookingSchedule.dateend}
                                onChange={setProperty}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectUser
                                caption={t('User')}
                                user={
                                    bookingSchedule.user as Parameters<
                                        typeof SttSelectUser
                                    >[0]['user']
                                }
                                onSelect={(selected) => {
                                    const mship = selected as Mship;
                                    if (mship) {
                                        setProperty({
                                            name: 'user',
                                            value: {
                                                fullName: mship.fullName,
                                                id: mship.user.id,
                                            },
                                        });
                                        if (errors.user) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                user: undefined,
                                            }));
                                        }
                                    }
                                }}
                                returnMship
                                error={!!errors.user}
                                helperText={errors.user}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectFacility
                                disabled
                                caption={t('Facility')}
                                defVal={
                                    bookingSchedule.facility &&
                                    bookingSchedule.facility.id
                                }
                                onChange={() => {}}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectWeekdays
                                name="weekdays"
                                selected={bookingSchedule.weekdays}
                                onChange={setProperty}
                            />
                        </Grid>
                    </Grid>
                }
            />
            <SttDialog
                open={showCreatedInfo}
                onClose={() => {
                    setShowCreatedInfo(false);
                    saveAndClose();
                }}
                title={t('additionalInfo')}
                buttons={[
                    {
                        type: 'accept',
                        caption: t('Understood'),
                        onClick: () => {
                            setShowCreatedInfo(false);
                            saveAndClose();
                        },
                    },
                ]}
                content={
                    <Box>
                        <p>{t('app.bookings.seeProgressInBookingTable')}</p>
                    </Box>
                }
            />
        </>
    );
}
